// AmsOutreach.tsx — automated engagement for 30–59 day accounts.
//
// Every number here is counted from `arrears_sends` rows, not from a stored statistic, so a
// channel that looks like it is working can be traced back to the individual sends that say so.
//
// The unconfirmed count has its own treatment. "Triggered, no confirmation" is the failure mode of
// trigger-and-confirm: we asked TCMS to send and do not know whether the resident received it.
// Folding it into "sent" would hide exactly the thing someone needs to act on.
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError } from '../../lib/api'
import DataTable from '../DataTable'

type Data = Awaited<ReturnType<typeof api.ams.outreach>>

export default function AmsOutreach() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!org) return
    api.ams.outreach(org).then(setD)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }, [org])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : '—')

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Outreach</h2>
          <p className="muted">
            Automated reminders for accounts 30–59 days overdue. Channel and timing follow the risk
            level. TCMS performs every send; AMS triggers it and records the confirmation.
          </p>
        </div>
      </header>

      <div className="grid-4">
        <div className="card kpi">
          <div className="kpi-label">Accounts in scope</div>
          <div className="kpi-n">{d.eligibleAccounts.toLocaleString()}</div>
          <div className="muted sm">30–59 days overdue</div>
        </div>
        {d.channels.map(c => (
          <div key={c.channel} className="card kpi">
            <div className="kpi-label">{c.channel}</div>
            <div className="kpi-n">{c.sent.toLocaleString()}</div>
            <div className="muted sm">
              {c.read > 0 ? `${pct(c.read, c.sent)} read` : 'no read receipts on this channel'}
            </div>
            {c.unconfirmed > 0 && (
              <div className="sm" style={{ color: 'var(--danger)' }}>
                {c.unconfirmed} unconfirmed by TCMS
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid-2">
        <section className="card">
          <h3>Message rules</h3>
          <p className="muted sm">
            Policy, held as data. Changing how a Town Council engages residents should not need a
            release.
          </p>
          {/* Message and "if no response" are prose, so each takes a full line on a phone
              rather than being squeezed into a column a few characters wide. */}
          <DataTable
            rows={d.rules}
            rowKey={r => r.risk}
            empty="No message rules configured."
            columns={[
              { key: 'risk', header: 'Risk', role: 'primary',
                cell: r => (
                  <span className={`tag ${r.risk === 'high' ? 'stop'
                    : r.risk === 'medium' ? 'warn' : 'c2'}`}>{r.risk}</span>
                ) },
              { key: 'day', header: 'From day', num: true, cell: r => r.day },
              { key: 'channel', header: 'Channel', cell: r => r.channel },
              { key: 'message', header: 'Message', wide: true,
                cell: r => <span className="muted sm">{r.message}</span> },
              { key: 'ifno', header: 'If no response', wide: true,
                cell: r => <span className="muted sm">{r.ifNoResponse}</span> },
            ]}
          />
        </section>

        <section className="card">
          <h3>Promises to pay</h3>
          <p className="muted sm">
            A promise is a date the resident agreed to. Repeatedly broken promises are the clearest
            signal of a chronic case rather than a struggling one.
          </p>
          <ul className="bars">
            {([['open', d.promises.open], ['kept', d.promises.kept],
               ['broken', d.promises.broken]] as const).map(([k, n]) => (
              <li key={k}>
                <span>{k}</span>
                <div className="bar">
                  <i className={k === 'broken' ? 'c3' : k === 'kept' ? 'c2' : ''}
                     style={{ width: `${d.promises.total ? (100 * n) / d.promises.total : 0}%` }} />
                </div>
                <span>{n.toLocaleString()}</span>
              </li>
            ))}
          </ul>
          {d.promises.kept === 0 && d.promises.total > 0 && (
            <p className="notice">
              No promises are recorded as kept. A promise is only marked kept when a payment lands
              against it, and that reconciliation is not built yet — so this is a gap in the system,
              not a finding about residents.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
